'use client';

import { Close, Content, DialogPortal, Overlay, Root, Title, Trigger } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import clsx from 'clsx';
import React from 'react';
import { Typography } from '../typography/Typography';
import { BasicClose } from '@woonstad/design-system';

const Popup = Root;
const PopupClose = Close;
const PopupTitle = Title;

type PopupTriggerProps = {
  children: React.ReactNode;
} & React.ComponentPropsWithoutRef<typeof Trigger>;

const PopupTrigger = React.forwardRef<
  React.ElementRef<typeof Trigger>,
  PopupTriggerProps
>(({
  children,
  ...props
}, ref) => (
  <Trigger ref={ref} {...props} className='relative'>
    {children}
  </Trigger>
));

PopupTrigger.displayName = Trigger.displayName;

const sizeVariants = {
  xsmall: 'w-full h-full p-5',
  small: 'w-full h-full md:h-auto md:max-w-[480px] p-5',
  normal: 'w-full h-full md:h-auto md:max-w-[600px] p-[40px]',
} as const;

type PopupContentProps = {
  overlayClassNames?: string;
  title: string;
  size?: keyof typeof sizeVariants;
  withCloseButton?: boolean;
  withTitle?: boolean;
  withIconTitle?: boolean;
  iconTitle?: string;
} & React.ComponentPropsWithoutRef<typeof Content>;

const PopupContent = React.forwardRef<
  React.ElementRef<typeof Content>,
  PopupContentProps
>(({
  className,
  overlayClassNames,
  children,
  size = 'normal',
  withTitle = true,
  withCloseButton = true,
  ...props
}, ref) => (
  <DialogPortal>
    <Overlay
        className={clsx(
          'fixed inset-0 z-50 bg-[black]/75 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          overlayClassNames,
        )}
      />
    {/*
     * Do NOT wrap Content in an intermediate <div>: DialogPortal wraps every
     * direct child in <Presence>, which UNMOUNTs immediately when the element
     * has `animation-name: none`.
     *
     * Uses custom `dialog-*` keyframes (see tailwind-base.css) that bake
     * translate(-50%, -50%) into every animation frame so centering is
     * preserved throughout enter/exit.
     */}
    <Content
      ref={ref}
      className={clsx(
        'dialog-content-popup dialog-centered fixed left-1/2 top-1/2 z-50 flex flex-col gap-4 bg-[white] p-6',
        sizeVariants[size],
        className,
      )}
      {...props}
    >
      <VisuallyHidden>
        <PopupTitle>{props.title}</PopupTitle>
      </VisuallyHidden>
      <div className="flex justify-between items-center">
        {withTitle && <Typography variant="title-block">{props.title}</Typography>}
        {/* TODO: Add icon title */}
        {/* {props.withIconTitle && <BasicIcon icon={props.iconTitle} />} */}
        {withCloseButton && (
            <PopupClose className="cursor-pointer w-fit ml-auto">
                <BasicClose className="text-primary-on-light size-6" />
            </PopupClose>
        )}
      </div>
      {children}
    </Content>
  </DialogPortal>
));

PopupContent.displayName = Content.displayName;

export {
  Popup,
  PopupClose,
  PopupContent,
  PopupTitle,
  PopupTrigger,
};
